import { Box, Button, Divider, Heading, Switch } from '@youversion/design-system-components';
import ArrowTwoCircle from '@youversion/design-system-icons/lib/ArrowTwoCircle';
import ChevronRightStroked from '@youversion/design-system-icons/lib/ChevronRightStroked';
import TriangleDownStroked from '@youversion/design-system-icons/lib/TriangleDownStroked';
import GiveOptionListItem from 'components/ui/controls/GiveOptionListItem';
import FrequencyDialog from 'components/ui/dialogs/FrequencyDialog';
import FundDialog from 'components/ui/dialogs/FundDialog';
import PaymentMethodsDialog from 'components/ui/dialogs/PaymentMethodsDialog';
import ProcessDateDialog from 'components/ui/dialogs/ProcessDateDialog';
import { PaymentMethodImage } from 'components/views/giving/payment-method-image';
import { format, isToday } from 'date-fns';
import { isFuture, isValid, startOfToday } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import { usePaymentMethodLabel } from 'hooks/usePaymentMethodLabel';
import { useQueryParams } from 'hooks/useQueryParams';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Screen } from 'src/utils/firebase/types';
import { donationValidation, keyCodes } from 'utils/constants';
import { firebaseLogEvent } from 'utils/firebase';
import { titleize } from 'utils/helpers';
import { useAmount } from 'utils/store/amount';
import { getCurrencyPart, useCurrency } from 'utils/store/currency';
import { useFrequency } from 'utils/store/frequency';
import { useFund } from 'utils/store/fund';
import { usePaymentDate } from 'utils/store/paymentDate';

interface CurrencySymbolProps {
  color: string;
  currencySymbolRef: React.RefObject<HTMLInputElement | null>;
  symbol: string;
}

function CurrencySymbol({ symbol, currencySymbolRef, color }: CurrencySymbolProps) {
  const { t } = useTranslation('common');
  return (
    <div
      className={`flex-none mbs-1 text-23 md:text-28 lg:text-33 ${color}`}
      ref={currencySymbolRef}
      title={t('giving.titles.currency')}
    >
      {symbol}
    </div>
  );
}

interface Props {
  amountInputRef: RefObject<HTMLInputElement | null>;
  blurInput: () => void;
  focusInput: () => void;
  frequencyDialogOpen: boolean;
  fundDialogOpen: boolean;
  isAmountInputFocused: boolean;
  isScheduledGift?: boolean;
  onGivingMethodClick?: null | (() => void);
  paymentMethodDialogOpen: boolean;
  processDateDialogOpen: boolean;
  screen: Screen;
  setFrequencyDialogOpen: Dispatch<SetStateAction<boolean>>;
  setFundDialogOpen: Dispatch<SetStateAction<boolean>>;
  setPaymentMethodDialogOpen: Dispatch<SetStateAction<boolean>>;
  setProcessDateDialogOpen: Dispatch<SetStateAction<boolean>>;
  title?: string;
}

export default function GiveForm({
  amountInputRef,
  blurInput,
  focusInput,
  frequencyDialogOpen,
  fundDialogOpen,
  isAmountInputFocused,
  isScheduledGift = false,
  onGivingMethodClick,
  paymentMethodDialogOpen,
  processDateDialogOpen,
  screen,
  setFrequencyDialogOpen,
  setFundDialogOpen,
  setPaymentMethodDialogOpen,
  setProcessDateDialogOpen,
  title,
}: Props) {
  const { i18n, t } = useTranslation('common');
  const { formatAmount, getCurrencySymbol, symbolPlacement, selectedCurrency } = useCurrency();
  const { selectedFrequency, getFrequencyName, setFrequency, isRecurring } = useFrequency();
  const { selectedFund, getFundName } = useFund();
  const { selectedAmount, setAmount } = useAmount();
  const { selectedPaymentDate, setPaymentDate } = usePaymentDate();
  const amountInputContainerRef = useRef<HTMLInputElement>(null);
  const currencySymbolRef = useRef<HTMLInputElement>(null);
  const fundButtonRef = useRef<HTMLButtonElement>(null);
  const givingMethodLinkRef = useRef<HTMLAnchorElement>(null);
  const tmpInputRef = useRef<HTMLInputElement>(null);
  const [isWrapping, setIsWrapping] = useState(false);
  const { language: locale } = i18n;
  const paymentMethodLabel = usePaymentMethodLabel();
  const { payment_date } = useQueryParams();

  const amountTooSmall = useMemo(
    () =>
      (selectedAmount !== '' && parseFloat(selectedAmount) < donationValidation.minimumAmount) ||
      selectedAmount === '.',
    [selectedAmount]
  );

  useEffect(() => {
    let initialDate = startOfToday();
    if (payment_date) {
      const timestamp = Number(payment_date);
      if (!isNaN(timestamp)) {
        const parsedDate = new Date(timestamp);
        if (isValid(parsedDate) && isFuture(parsedDate)) {
          initialDate = parsedDate;
        }
      }
    }
    setPaymentDate(initialDate);
  }, [payment_date, setPaymentDate]);

  const inputTextClass = 'align-top leading-0 text-start p-0 m-0 font-bold text-48 md:text-69';
  const inputColor = (() => {
    if (amountTooSmall) {
      return 'text-alert';
    } else if (selectedAmount != '' || isAmountInputFocused) {
      return 'text-green-30';
    }
    return 'text-text-light dark:text-text-dark';
  })();
  const inputBorderColor = (() => {
    if (amountTooSmall) {
      return 'border-alert';
    } else if (isAmountInputFocused) {
      return 'border-green-30';
    }
    return 'border-transparent hover:border-tertiary-light dark:hover:border-tertiary-dark';
  })();

  function formatInput(amount: string, currency: string, language: string): string {
    if (amount === '') {
      return '';
    }

    const decimalSeparator = getCurrencyPart('decimal', language, currency)?.value || '';
    if (amount === '.') {
      return decimalSeparator;
    }

    const [integerPart, fractionPart] = amount.split('.');
    const decimalPlaces = fractionPart?.length || 0;
    const formattedAmount = formatAmount({ amount: parseFloat(amount), decimalPlaces }).replace(
      getCurrencySymbol(currency),
      ''
    );

    // if input is like '.12'
    if (!integerPart?.length && decimalSeparator) {
      return decimalSeparator + formattedAmount.split(decimalSeparator)[1];
    }

    // if input is like '12.'
    if (amount.includes('.') && decimalPlaces === 0) {
      return formattedAmount + decimalSeparator;
    }

    return formattedAmount;
  }

  function handleAmountFieldKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === keyCodes.ENTER) {
      event.currentTarget.blur();
    }
    firebaseLogEvent('GivingButtonTap', { label: 'Keyboard', screen: 'giving_form', value: event.key });
  }

  function handleGivingMethodClick(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    if (onGivingMethodClick) {
      onGivingMethodClick();
    } else {
      setPaymentMethodDialogOpen(true);
    }
  }

  /**
   * Adjust the width of the amount input field based on content width,
   * up to a max width of the available width from its parent container.
   */
  useEffect(() => {
    if (
      !amountInputRef.current ||
      !currencySymbolRef.current ||
      !amountInputContainerRef.current ||
      !tmpInputRef.current ||
      !fundButtonRef.current
    ) {
      return;
    }
    if (!isWrapping) {
      const amountInputWidth = Math.ceil(tmpInputRef.current.getBoundingClientRect().width);
      const currencySymbolWidth = Math.ceil(currencySymbolRef.current.getBoundingClientRect().width);
      const amountInputContainerWidth = Math.floor(amountInputContainerRef.current.getBoundingClientRect().width);
      const fundButtonWidth = Math.ceil(fundButtonRef.current.getBoundingClientRect().width);
      if (amountInputWidth + currencySymbolWidth + fundButtonWidth <= amountInputContainerWidth) {
        amountInputRef.current.style.width = `${amountInputWidth + 30}px`;
      }
    } else {
      const amountInputWidth = Math.ceil(tmpInputRef.current.getBoundingClientRect().width);
      const currencySymbolWidth = Math.ceil(currencySymbolRef.current.getBoundingClientRect().width);
      const amountInputContainerWidth = Math.floor(amountInputContainerRef.current.getBoundingClientRect().width);
      if (amountInputWidth + currencySymbolWidth <= amountInputContainerWidth) {
        amountInputRef.current.style.width = `${amountInputWidth + 30}px`;
      }
    }
  }, [selectedAmount, isWrapping, amountInputRef]);

  const checkWrap = useCallback(() => {
    const container = amountInputContainerRef.current;
    const text = amountInputRef.current;
    const button = fundButtonRef.current;
    const currency = currencySymbolRef.current;
    if (text && container && button && currency) {
      setIsWrapping(text.clientWidth + button.clientWidth + currency.clientWidth + 48 >= container.clientWidth);
    }
  }, [amountInputRef]);

  /**
   * Determines whether the fund button is currently wrapping below the amount input text.
   * We apply different styles based on whether is is wrapping or not.
   */
  useEffect(() => {
    window.addEventListener('resize', checkWrap);
    return () => window.removeEventListener('resize', checkWrap);
  }, [checkWrap]);
  useEffect(checkWrap, [selectedAmount, checkWrap]);

  return (
    <>
      <Box className='flex flex-col gap-3 z-1'>
        <Heading className='sr-only' level={1}>
          {title}
        </Heading>
        <div
          className={`flex text-clip items-center
          ${isWrapping ? 'flex-col justify-center' : 'flex-row justify-between'}
          gap-2 p-2 bg-canvas-light dark:bg-canvas-secondary-dark rounded-2 cursor-pointer
          border-standard ${inputBorderColor} transition duration-150`}
          onBlur={blurInput}
          onClick={() => {
            firebaseLogEvent('GivingButtonTap', { label: 'Amount', screen });
            focusInput();
          }}
          ref={amountInputContainerRef}
        >
          <Box className='flex max-w-full rtl:flex-row-reverse'>
            {symbolPlacement(selectedCurrency, i18n.language) === 'before' ? (
              <CurrencySymbol
                color={inputColor}
                currencySymbolRef={currencySymbolRef}
                symbol={getCurrencySymbol(selectedCurrency)}
              />
            ) : null}
            <input
              aria-label={t('giving.labels.amount')}
              autoComplete='off'
              autoFocus={true}
              className={`${inputTextClass} bg-canvas-light dark:bg-canvas-secondary-dark ${inputColor} outline-none cursor-pointer`}
              data-1p-ignore='true'
              data-testid='amount_to_give'
              dir='auto'
              id='amount_to_give'
              inputMode='decimal'
              name='amountToGive-search'
              onChange={event => setAmount(event.currentTarget.value)}
              onKeyDown={handleAmountFieldKeyDown}
              placeholder='0'
              ref={amountInputRef}
              type='text'
              value={formatInput(selectedAmount, selectedCurrency, locale)}
            />
            {symbolPlacement(selectedCurrency, i18n.language) === 'after' ? (
              <CurrencySymbol
                color={inputColor}
                currencySymbolRef={currencySymbolRef}
                symbol={getCurrencySymbol(selectedCurrency)}
              />
            ) : null}
            <span
              className={`text-transparent ${inputTextClass} invisible pointer-events-none absolute -z-1`}
              ref={tmpInputRef}
            >
              {selectedAmount !== '' ? selectedAmount : '0'}
            </span>
          </Box>
          <Button
            icon={TriangleDownStroked}
            iconPosition='end'
            id='id_give_fund'
            onClick={event => {
              event.stopPropagation();
              firebaseLogEvent('GivingButtonTap', { label: 'Fund', screen });
              blurInput();
              setFundDialogOpen(true);
            }}
            ref={fundButtonRef}
            size='sm'
            title={t('giving.titles.fund')}
            variant='secondary'
          >
            {getFundName(selectedFund)}
          </Button>
        </div>
        <Box className='bg-canvas-light dark:bg-canvas-secondary-dark rounded-2' data-testid='give_options_container'>
          <GiveOptionListItem
            className='plb-1 pli-2'
            label={t('giving.labels.make_gift_recurring')}
            rightElement={
              <Switch
                checked={isRecurring}
                data-testid='make_recurring_switch'
                onChange={() => {
                  firebaseLogEvent('GivingButtonTap', {
                    label: 'Recurring Toggle',
                    screen,
                    value: isRecurring ? 'On' : 'Off',
                  });
                  isRecurring ? setFrequency('ONETIME') : setFrequency('MONTHLY');
                }}
              />
            }
            startIcon={<ArrowTwoCircle className='mie-0 text-text-light dark:text-text-dark' />}
          />
          <Divider className='border-canvas-secondary-light border-small' />
          {isRecurring ? (
            <GiveOptionListItem
              className='plb-1 pli-2'
              id='id_give_frequency'
              label={titleize(getFrequencyName(selectedFrequency))}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Frequency', screen });
                setFrequencyDialogOpen(true);
              }}
              rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
              title={t('giving.titles.frequency')}
            />
          ) : null}
          <GiveOptionListItem
            className='plb-1 pli-2'
            label={isToday(selectedPaymentDate) ? `${t('giving.labels.today')}` : format(selectedPaymentDate, 'PP')}
            onClick={() => {
              firebaseLogEvent('GivingButtonTap', { label: 'Process Date', screen });
              setProcessDateDialogOpen(true);
            }}
            rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
            title={t('giving.titles.process_date')}
            value={selectedFrequency}
          />
        </Box>
        <Box className='bg-canvas-light dark:bg-canvas-secondary-dark rounded-1'>
          <Link href='/payment-methods/add' onClick={handleGivingMethodClick} ref={givingMethodLinkRef} tabIndex={0}>
            <GiveOptionListItem
              className='plb-1 pli-2'
              data-testid='selected_payment_method'
              label={paymentMethodLabel}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Payment Method', screen });
                setPaymentMethodDialogOpen(true);
              }}
              rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
              startIcon={<PaymentMethodImage />}
            />
          </Link>
        </Box>
      </Box>
      <AnimatePresence>
        {fundDialogOpen ? <FundDialog onClose={() => setFundDialogOpen(false)} /> : null}
      </AnimatePresence>
      <AnimatePresence>
        {paymentMethodDialogOpen ? (
          <PaymentMethodsDialog key='paymentMethodsDialog' onClose={() => setPaymentMethodDialogOpen(false)} />
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {frequencyDialogOpen ? <FrequencyDialog onClose={() => setFrequencyDialogOpen(false)} /> : null}
      </AnimatePresence>
      <AnimatePresence>
        {processDateDialogOpen ? (
          <ProcessDateDialog isScheduledGift={isScheduledGift} onClose={() => setProcessDateDialogOpen(false)} />
        ) : null}
      </AnimatePresence>
    </>
  );
}
